import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import PageActions from '../components/PageActions';
import AddressSuggestInput from '../components/AddressSuggestInput';
import { apiUrl, mediaUrl } from '../services/api';

const EMPTY_IDEA = { title: '', address: '', lat: null, lon: null, url: '', notes: '' };

// ---------------------------------------------------------------------------
// Append best-effort autoplay (with sound) to an embed URL. Browser autoplay
// policy allows sound here because opening the modal is a user gesture.
function withAutoplay(url, platform) {
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  if (platform === 'youtube') return `${url}${sep}autoplay=1&mute=0&playsinline=1&rel=0`;
  return `${url}${sep}autoplay=1`;
}

// Lightbox / clip player modal — near-native TikTok/YouTube layout
// ---------------------------------------------------------------------------
function ClipModal({ clip, clips, destinations, labels, allActivities, onClose, onNavigate, onDelete, onCreateWishlist, onUpdateDestinations, onAddLabel, onRemoveLabel, onLinkActivity, onUnlinkActivity, onLinkWishlist, onUnlinkWishlist }) {
  const overlayRef = useRef(null);
  const [newIdea, setNewIdea] = useState(EMPTY_IDEA);
  const [ideaExpanded, setIdeaExpanded] = useState(false);
  const isTikTok = clip.platform === 'tiktok';
  const currentIndex = clips.findIndex(c => c.clip_id === clip.clip_id);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      // Don't hijack arrow keys while the user is typing in a field.
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        const next = clips[currentIndex + 1];
        if (next) onNavigate(next);
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const prev = clips[currentIndex - 1];
        if (prev) onNavigate(prev);
      }
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    // Focus the modal so arrow-key navigation works immediately, even before
    // the user clicks anything (the cross-origin video iframe can otherwise
    // swallow keyboard focus).
    if (overlayRef.current) overlayRef.current.focus();
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onNavigate, clips, currentIndex]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const toggleDest = (destId) => {
    const current = clip.destinations.map(d => d.destination_id);
    const next = current.includes(destId)
      ? current.filter(id => id !== destId)
      : [...current, destId];
    onUpdateDestinations(clip.clip_id, next);
  };

  const toggleLabel = (labelId) => {
    const active = (clip.labels || []).some(l => l.label_id === labelId);
    if (active) onRemoveLabel(clip.clip_id, labelId);
    else onAddLabel(clip.clip_id, labelId);
  };

  const linkedActivityIds = new Set(clip.activity_ids || []);
  const linkedIdeaIds = new Set(clip.idea_ids || []);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < clips.length - 1;

  const { itineraryActivities = [], wishlistItems = [] } = allActivities;

  const modal = (
    <div
      ref={overlayRef}
      tabIndex={-1}
      onClick={handleOverlayClick}
      style={{
        outline: 'none',
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex',
      }}
    >
      {/* Up/down nav arrows */}
      {hasPrev && (
        <button onClick={() => onNavigate(clips[currentIndex - 1])} aria-label="Previous" style={arrowStyle('top')}>▲</button>
      )}
      {hasNext && (
        <button onClick={() => onNavigate(clips[currentIndex + 1])} aria-label="Next" style={arrowStyle('bottom')}>▼</button>
      )}

      {/* Close */}
      <button type="button" onClick={onClose} aria-label="Close" style={{
        position: 'fixed', top: 16, right: 20, zIndex: 10001,
        background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
        width: 40, height: 40, cursor: 'pointer', color: '#fff', fontSize: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>×</button>

      {/* Delete */}
      <button type="button" onClick={() => onDelete(clip.clip_id)} aria-label="Delete clip" title="Remove this clip" style={{
        position: 'fixed', top: 16, right: 68, zIndex: 10001,
        background: 'rgba(220,38,38,0.85)', border: 'none', borderRadius: '50%',
        width: 40, height: 40, cursor: 'pointer', color: '#fff', fontSize: 18,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>🗑</button>

      {/* Inner layout — stops overlay click */}
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', width: '100%', height: '100%' }}>

        {/* ── Video panel ── */}
        {clip.embed_url && (
          <div style={{
            height: '100vh',
            width: isTikTok ? 'min(calc(100vh * 9 / 16), 55vw)' : undefined,
            flex: isTikTok ? '0 0 auto' : 1,
            background: '#000',
            flexShrink: 0,
            overflow: 'hidden',
            position: 'relative',
          }}>
            <iframe
              key={clip.clip_id}
              src={withAutoplay(clip.embed_url, clip.platform)}
              title={clip.title}
              allow="autoplay; fullscreen; clipboard-write; encrypted-media; picture-in-picture"
              allowFullScreen
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
            {/* Black mask covers TikTok's card UI (caption, music, watch now) at the bottom */}
            {isTikTok && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                height: '28%',
                background: 'linear-gradient(to bottom, transparent 0%, #000 35%)',
                pointerEvents: 'none',
                zIndex: 2,
              }} />
            )}
          </div>
        )}

        {/* ── Sidebar ── */}
        <div style={{
          flex: 1, minWidth: 280, maxWidth: 420,
          height: '100vh', background: '#1a1a1a',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Header — kept compact so a long caption never crowds out the
              actions below. The full caption lives in the scrollable body. */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #2a2a2a', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <PlatformBadge platform={clip.platform} />
            {clip.author_name && (
              <div style={{ fontSize: 13, color: '#888' }}>@{clip.author_name}</div>
            )}
          </div>

          {/* Scrollable body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 22 }}>
            <a href={clip.url} target="_blank" rel="noopener noreferrer" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
              color: '#fff', fontWeight: 600, textDecoration: 'none', width: 'fit-content',
              background: isTikTok ? '#111' : '#c00', padding: '8px 14px', borderRadius: 6,
              border: `1px solid ${isTikTok ? '#333' : '#c00'}`,
            }}>
              {isTikTok ? '▶  Watch on TikTok' : '▶  Watch on YouTube'}
            </a>

            {/* Caption / description — collapsible so long captions don't bury
                the tagging actions below. */}
            <ClipDescription text={clip.title} />

            {/* Destination tags */}
            {destinations.length > 0 && (
              <div>
                <SectionLabel>Destinations</SectionLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {destinations.map(d => {
                    const active = clip.destinations.some(cd => cd.destination_id === d.destination_id);
                    return (
                      <button key={d.destination_id} type="button" onClick={() => toggleDest(d.destination_id)}
                        style={{
                          padding: '5px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                          border: active ? '1px solid var(--tp-accent)' : '1px solid #444',
                          background: active ? 'var(--tp-accent)' : 'transparent',
                          color: active ? '#fff' : '#aaa', transition: 'all 0.15s',
                        }}>
                        {d.destination_name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Categories */}
            {(labels || []).length > 0 && (
              <div>
                <SectionLabel>Categories</SectionLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {labels.map(l => {
                    const active = (clip.labels || []).some(cl => cl.label_id === l.label_id);
                    return (
                      <button key={l.label_id} type="button" onClick={() => toggleLabel(l.label_id)}
                        style={{
                          padding: '5px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                          border: active ? `1px solid ${l.color || '#888'}` : '1px solid #444',
                          background: active ? (l.color || '#888') : 'transparent',
                          color: active ? '#fff' : '#aaa', transition: 'all 0.15s',
                        }}>
                        {l.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Itinerary activities */}
            {itineraryActivities.length > 0 && (
              <div>
                <SectionLabel>Link to itinerary</SectionLabel>
                <LinkList
                  items={itineraryActivities}
                  idKey="activity_id"
                  linkedIds={linkedActivityIds}
                  onLink={id => onLinkActivity(clip.clip_id, id)}
                  onUnlink={id => onUnlinkActivity(clip.clip_id, id)}
                />
              </div>
            )}

            {/* Research / wishlist items */}
            <div>
              <SectionLabel>Link to research</SectionLabel>
              {wishlistItems.length > 0 && (
                <LinkList
                  items={wishlistItems}
                  idKey="idea_id"
                  linkedIds={linkedIdeaIds}
                  onLink={id => onLinkWishlist(clip.clip_id, id)}
                  onUnlink={id => onUnlinkWishlist(clip.clip_id, id)}
                />
              )}
              {/* Create a new research item (with details) from this clip and link it */}
              {(() => {
                const fieldStyle = {
                  width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 6,
                  background: '#111', color: '#fff', border: '1px solid #333',
                };
                const reset = () => { setNewIdea(EMPTY_IDEA); setIdeaExpanded(false); };
                const submit = () => {
                  if (!newIdea.title.trim()) return;
                  onCreateWishlist(clip, newIdea);
                  reset();
                };
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    <input
                      value={newIdea.title}
                      onChange={e => setNewIdea(prev => ({ ...prev, title: e.target.value }))}
                      onFocus={() => setIdeaExpanded(true)}
                      onKeyDown={e => { if (e.key === 'Enter' && !ideaExpanded) submit(); }}
                      placeholder="Add a research item…"
                      style={fieldStyle}
                    />
                    {ideaExpanded && (
                      <>
                        <AddressSuggestInput
                          value={newIdea.address}
                          placeholder="Location / address (optional)"
                          onChange={v => setNewIdea(prev => ({ ...prev, address: v, lat: null, lon: null }))}
                          onSelect={s => setNewIdea(prev => ({
                            ...prev,
                            address: s.display_name || s.label || prev.address,
                            lat: s.lat != null ? s.lat : null,
                            lon: s.lon != null ? s.lon : null,
                          }))}
                        />
                        <input
                          value={newIdea.url}
                          onChange={e => setNewIdea(prev => ({ ...prev, url: e.target.value }))}
                          placeholder="Research link / URL (optional)"
                          style={fieldStyle}
                        />
                        <textarea
                          value={newIdea.notes}
                          onChange={e => setNewIdea(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Notes (optional)"
                          rows={2}
                          style={{ ...fieldStyle, resize: 'vertical' }}
                        />
                      </>
                    )}
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {ideaExpanded && (
                        <button type="button" onClick={reset} style={{
                          padding: '8px 14px', fontSize: 13, borderRadius: 6, cursor: 'pointer',
                          background: 'transparent', color: '#aaa', border: '1px solid #333',
                        }}>Cancel</button>
                      )}
                      <button
                        type="button"
                        disabled={!newIdea.title.trim()}
                        onClick={submit}
                        style={{
                          padding: '8px 14px', fontSize: 13, borderRadius: 6, cursor: 'pointer',
                          background: 'var(--tp-accent)', color: '#fff', border: 'none',
                          opacity: newIdea.title.trim() ? 1 : 0.5,
                        }}
                      >
                        Add to research
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Counter */}
          {clips.length > 1 && (
            <div style={{ padding: '10px 20px', borderTop: '1px solid #2a2a2a', fontSize: 12, color: '#555', textAlign: 'center' }}>
              {currentIndex + 1} / {clips.length} · ↑↓ to navigate
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

function arrowStyle(pos) {
  return {
    position: 'fixed', [pos]: 20, left: '50%', transform: 'translateX(-50%)',
    zIndex: 10001, background: 'rgba(255,255,255,0.15)', border: 'none',
    borderRadius: '50%', width: 40, height: 40, cursor: 'pointer',
    color: '#fff', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#555', marginBottom: 10 }}>
      {children}
    </div>
  );
}

// Clip caption with a "See more"/"See less" toggle. Captions (especially
// TikTok) can be enormous; clamp to a few lines by default so the modal's
// actions stay reachable, and let the user expand inline like a social app.
function ClipDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  const t = (text || '').trim();
  if (!t) return null;
  const isLong = t.length > 200;
  const clamp = isLong && !expanded;
  return (
    <div>
      <div
        style={{
          fontSize: 14, lineHeight: 1.5, color: '#ddd',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          ...(clamp ? { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}),
        }}
      >
        {t}
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          style={{
            marginTop: 4, padding: 0, background: 'none', border: 'none',
            color: '#888', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
}

function LinkList({ items, idKey, linkedIds, onLink, onUnlink }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {items.map(item => {
        const id = item[idKey];
        const linked = linkedIds.has(id);
        return (
          <div key={id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '7px 10px', borderRadius: 8, gap: 8, transition: 'all 0.15s',
            background: linked ? 'rgba(100,160,255,0.15)' : '#252525',
            border: linked ? '1px solid rgba(100,160,255,0.3)' : '1px solid transparent',
          }}>
            <span style={{ fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#ddd' }}>
              {item.title}
            </span>
            <button type="button" onClick={() => linked ? onUnlink(id) : onLink(id)} style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer',
              border: linked ? '1px solid #e05' : '1px solid var(--tp-accent)',
              background: 'transparent', color: linked ? '#e05' : 'var(--tp-accent)',
              whiteSpace: 'nowrap', fontWeight: 600,
            }}>
              {linked ? 'Unlink' : 'Link'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Platform badge
// ---------------------------------------------------------------------------
function PlatformBadge({ platform }) {
  if (!platform || platform === 'unknown') return null;
  const styles = {
    tiktok: { bg: '#010101', color: '#fff', label: 'TikTok' },
    youtube: { bg: '#ff0000', color: '#fff', label: 'YouTube' },
    instagram: { bg: '#c13584', color: '#fff', label: 'Instagram' },
  };
  const s = styles[platform] || { bg: '#555', color: '#fff', label: platform };
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 700,
      padding: '2px 6px', borderRadius: 4,
      background: s.bg, color: s.color,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>
      {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Single clip card
// ---------------------------------------------------------------------------
function ClipCard({ clip, onClick }) {
  // Fall back to the ▶ placeholder if the cover fails to load — TikTok hands out
  // signed CDN URLs that expire, so a stored remote thumbnail can 404/400 later.
  const [imgFailed, setImgFailed] = useState(false);
  const showThumb = clip.thumbnail_url && !imgFailed;
  return (
    <div
      className="clip-card"
      onClick={onClick}
      style={{
        cursor: 'pointer',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'var(--tp-surface)',
        border: '1px solid var(--tp-border)',
        transition: 'transform 0.15s, box-shadow 0.15s',
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      {/* Thumbnail */}
      <div style={{ position: 'relative', paddingBottom: clip.platform === 'tiktok' ? '177%' : '56.25%', background: '#111', overflow: 'hidden' }}>
        {showThumb
          ? <img src={mediaUrl(clip.thumbnail_url)} alt={clip.title} onError={() => setImgFailed(true)} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tp-muted)', fontSize: 32 }}>▶</div>
        }
        {/* Play overlay */}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.3)', opacity: 0, transition: 'opacity 0.15s',
        }}
          className="clip-play-overlay"
        >
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#111', fontSize: 18, marginLeft: 3 }}>▶</span>
          </div>
        </div>
        {/* Platform badge (top-left) */}
        <div style={{ position: 'absolute', top: 8, left: 8 }}>
          <PlatformBadge platform={clip.platform} />
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {clip.title || clip.url}
        </div>
        {clip.author_name && (
          <div style={{ fontSize: 11, color: 'var(--tp-muted)' }}>@{clip.author_name}</div>
        )}
        {(clip.labels || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {clip.labels.map(l => (
              <span key={l.label_id} style={{
                fontSize: 10, padding: '2px 7px', borderRadius: 20,
                background: l.color || '#888', color: '#fff', fontWeight: 600,
              }}>
                {l.name}
              </span>
            ))}
          </div>
        )}
        {clip.destinations.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {clip.destinations.map(d => (
              <span key={d.destination_id} style={{
                fontSize: 10, padding: '2px 7px', borderRadius: 20,
                border: '1px solid var(--tp-border)', color: 'var(--tp-muted)', fontWeight: 600,
              }}>
                {d.destination_name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add clip form
// ---------------------------------------------------------------------------
function AddClipForm({ destinations, labels, onAdd }) {
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [selectedDests, setSelectedDests] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchPreview = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const res = await fetch(apiUrl(`/api/oembed?url=${encodeURIComponent(url.trim())}`));
      const data = await res.json();
      if (data.platform === 'unknown') {
        setError('Could not recognize this URL. Paste a TikTok or YouTube link.');
      } else {
        setPreview(data);
      }
    } catch {
      setError('Failed to fetch preview. Check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!preview || !url.trim()) return;
    setSaving(true);
    try {
      await onAdd(url.trim(), selectedDests, selectedLabels);
      setUrl('');
      setPreview(null);
      setSelectedDests([]);
      setSelectedLabels([]);
    } finally {
      setSaving(false);
    }
  };

  const toggleDest = (id) => setSelectedDests(prev =>
    prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
  );

  const toggleLabel = (id) => setSelectedLabels(prev =>
    prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]
  );

  return (
    <div className="tp-card" style={{ marginBottom: 24 }}>
      <div style={{ fontWeight: 600, marginBottom: 12 }}>Add clip</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          type="url"
          placeholder="Paste a TikTok or YouTube URL…"
          value={url}
          onChange={e => { setUrl(e.target.value); setPreview(null); setError(''); }}
          onKeyDown={e => { if (e.key === 'Enter') fetchPreview(); }}
          style={{ flex: 1 }}
        />
        <button type="button" onClick={fetchPreview} disabled={loading || !url.trim()}>
          {loading ? 'Loading…' : 'Preview'}
        </button>
      </div>

      {error && <div style={{ color: 'var(--tp-danger, #e05)', fontSize: 13, marginBottom: 8 }}>{error}</div>}

      {preview && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: 12, background: 'var(--tp-surface-2)', borderRadius: 8, marginBottom: 12 }}>
          {preview.thumbnail_url && (
            <img src={mediaUrl(preview.thumbnail_url)} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <PlatformBadge platform={preview.platform} />
            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {preview.title}
            </div>
            {preview.author_name && <div style={{ fontSize: 11, color: 'var(--tp-muted)' }}>@{preview.author_name}</div>}
          </div>
        </div>
      )}

      {preview && destinations.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--tp-muted)', marginBottom: 6 }}>Tag destinations (optional)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {destinations.map(d => {
              const active = selectedDests.includes(d.destination_id);
              return (
                <button
                  key={d.destination_id}
                  type="button"
                  onClick={() => toggleDest(d.destination_id)}
                  style={{
                    padding: '4px 10px', borderRadius: 20, fontSize: 12,
                    border: active ? '1px solid var(--tp-accent)' : '1px solid var(--tp-border)',
                    background: active ? 'var(--tp-accent)' : 'transparent',
                    color: active ? '#fff' : 'var(--tp-muted)',
                    cursor: 'pointer',
                  }}
                >
                  {d.destination_name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {preview && labels.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--tp-muted)', marginBottom: 6 }}>Categories (optional)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {labels.map(l => {
              const active = selectedLabels.includes(l.label_id);
              return (
                <button
                  key={l.label_id}
                  type="button"
                  onClick={() => toggleLabel(l.label_id)}
                  style={{
                    padding: '4px 10px', borderRadius: 20, fontSize: 12,
                    border: active ? `1px solid ${l.color || '#888'}` : '1px solid var(--tp-border)',
                    background: active ? (l.color || '#888') : 'transparent',
                    color: active ? '#fff' : 'var(--tp-muted)',
                    cursor: 'pointer',
                  }}
                >
                  {l.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {preview && (
        <button type="button" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : 'Add to Inspiration'}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Inspiration() {
  const { tripId } = useParams();
  const [clips, setClips] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [labels, setLabels] = useState([]);
  const [itineraryActivities, setItineraryActivities] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [tripName, setTripName] = useState('');
  const [filterDestId, setFilterDestId] = useState('all');
  const [filterLabelId, setFilterLabelId] = useState('all');
  const [activeClip, setActiveClip] = useState(null);

  const fetchClips = useCallback(async () => {
    const res = await fetch(apiUrl(`/api/trips/${tripId}/clips`));
    if (res.ok) setClips(await res.json());
  }, [tripId]);

  useEffect(() => {
    fetchClips();
    fetch(apiUrl(`/api/trips/${tripId}/destinations`))
      .then(r => r.json()).then(setDestinations).catch(() => {});
    fetch(apiUrl(`/api/labels`))
      .then(r => r.ok ? r.json() : []).then(setLabels).catch(() => {});
    fetch(apiUrl(`/api/trips`))
      .then(r => r.json())
      .then(trips => {
        const t = trips.find(t => String(t.trip_id) === String(tripId));
        if (t) setTripName(t.trip_name);
      }).catch(() => {});
    fetch(apiUrl(`/api/trips/${tripId}/activities`))
      .then(r => r.ok ? r.json() : []).then(setItineraryActivities).catch(() => {});
    fetch(apiUrl(`/api/trips/${tripId}/wishlist`))
      .then(r => r.ok ? r.json() : []).then(setWishlistItems).catch(() => {});
  }, [tripId, fetchClips]);

  // Keep activeClip in sync with clips array after updates
  useEffect(() => {
    if (activeClip) {
      const updated = clips.find(c => c.clip_id === activeClip.clip_id);
      if (updated) setActiveClip(updated);
    }
  }, [clips]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async (url, destinationIds, labelIds) => {
    const res = await fetch(apiUrl(`/api/trips/${tripId}/clips`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, destination_ids: destinationIds, label_ids: labelIds }),
    });
    if (res.ok) fetchClips();
  };

  const handleDelete = async (clipId) => {
    if (!window.confirm('Remove this clip?')) return;
    await fetch(apiUrl(`/api/clips/${clipId}`), { method: 'DELETE' });
    setActiveClip(null);
    fetchClips();
  };

  const handleUpdateDestinations = async (clipId, destinationIds) => {
    await fetch(apiUrl(`/api/clips/${clipId}/destinations`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ destination_ids: destinationIds }),
    });
    fetchClips();
  };

  const handleAddLabel = async (clipId, labelId) => {
    await fetch(apiUrl(`/api/clips/${clipId}/labels`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label_id: labelId }),
    });
    fetchClips();
  };

  const handleRemoveLabel = async (clipId, labelId) => {
    await fetch(apiUrl(`/api/clips/${clipId}/labels/${labelId}`), { method: 'DELETE' });
    fetchClips();
  };

  const handleLinkActivity = async (clipId, activityId) => {
    await fetch(apiUrl(`/api/clips/${clipId}/activities/${activityId}`), { method: 'POST' });
    fetchClips();
  };

  const handleUnlinkActivity = async (clipId, activityId) => {
    await fetch(apiUrl(`/api/clips/${clipId}/activities/${activityId}`), { method: 'DELETE' });
    fetchClips();
  };

  const handleLinkWishlist = async (clipId, ideaId) => {
    await fetch(apiUrl(`/api/clips/${clipId}/wishlist/${ideaId}`), { method: 'POST' });
    fetchClips();
  };

  const handleUnlinkWishlist = async (clipId, ideaId) => {
    await fetch(apiUrl(`/api/clips/${clipId}/wishlist/${ideaId}`), { method: 'DELETE' });
    fetchClips();
  };

  // Create a new research/wishlist item from a clip and immediately link it.
  const handleCreateAndLinkWishlist = async (clip, draft) => {
    const d = (draft && typeof draft === 'object') ? draft : { title: draft };
    const t = String(d.title || '').trim();
    if (!t) return;
    const destId = (clip.destinations && clip.destinations[0] && clip.destinations[0].destination_id) || null;
    const res = await fetch(apiUrl(`/api/trips/${tripId}/wishlist`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: t,
        destination_id: destId,
        address: String(d.address || '').trim() || null,
        lat: d.lat ?? null,
        lon: d.lon ?? null,
        url: String(d.url || '').trim() || null,
        notes: String(d.notes || '').trim() || null,
      }),
    });
    if (!res.ok) return;
    const created = await res.json();
    if (created.idea_id) {
      await fetch(apiUrl(`/api/clips/${clip.clip_id}/wishlist/${created.idea_id}`), { method: 'POST' });
    }
    fetch(apiUrl(`/api/trips/${tripId}/wishlist`)).then(r => r.ok ? r.json() : []).then(setWishlistItems).catch(() => {});
    fetchClips();
  };

  const filteredClips = clips.filter(c => {
    const matchDest = filterDestId === 'all' || c.destinations.some(d => d.destination_id === filterDestId);
    const matchLabel = filterLabelId === 'all' || (c.labels || []).some(l => l.label_id === filterLabelId);
    return matchDest && matchLabel;
  });

  // Only offer category filters for labels actually used by this trip's clips.
  const usedLabels = (() => {
    const counts = new Map();
    clips.forEach(c => (c.labels || []).forEach(l => {
      const entry = counts.get(l.label_id) || { ...l, count: 0 };
      entry.count += 1;
      counts.set(l.label_id, entry);
    }));
    return [...counts.values()].sort((a, b) => a.name.localeCompare(b.name));
  })();

  // Group TikTok (portrait) and YouTube (landscape) separately for nicer layout
  const tiktokClips = filteredClips.filter(c => c.platform === 'tiktok');
  const otherClips = filteredClips.filter(c => c.platform !== 'tiktok');

  return (
    <>
      {/* Hover effect for play overlay — inject minimal CSS */}
      <style>{`.clip-card:hover .clip-play-overlay { opacity: 1 !important; }`}</style>

      <section className="tp-section">
        <div className="tp-hero">
          <p className="tp-hero__kicker">Inspiration</p>
          <h1 className="tp-hero__title">{tripName || 'Trip'}</h1>
        </div>
        <PageActions
          actions={[
            { label: 'Back to Home', to: '/', key: 'home' },
            { label: 'Itinerary', to: `/itinerary/${tripId}`, key: 'itinerary' },
            { label: 'Research', to: `/trips/${tripId}/research`, key: 'research' },
            { label: 'Trip Setup', to: `/trip-setup/${tripId}`, key: 'setup' },
          ]}
        />
      </section>

      <section className="tp-section">
        <AddClipForm destinations={destinations} labels={labels} onAdd={handleAdd} />

        {/* Filter bar */}
        {destinations.length > 0 && clips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
            {[{ destination_id: 'all', destination_name: `All (${clips.length})` }, ...destinations].map(d => (
              <button
                key={d.destination_id}
                type="button"
                onClick={() => setFilterDestId(d.destination_id)}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 13,
                  border: filterDestId === d.destination_id ? '1px solid var(--tp-accent)' : '1px solid var(--tp-border)',
                  background: filterDestId === d.destination_id ? 'var(--tp-accent)' : 'transparent',
                  color: filterDestId === d.destination_id ? '#fff' : 'inherit',
                  cursor: 'pointer',
                }}
              >
                {d.destination_name}
              </button>
            ))}
          </div>
        )}

        {/* Category filter bar */}
        {usedLabels.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
            <button
              type="button"
              onClick={() => setFilterLabelId('all')}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 13,
                border: filterLabelId === 'all' ? '1px solid var(--tp-accent)' : '1px solid var(--tp-border)',
                background: filterLabelId === 'all' ? 'var(--tp-accent)' : 'transparent',
                color: filterLabelId === 'all' ? '#fff' : 'inherit',
                cursor: 'pointer',
              }}
            >
              All categories
            </button>
            {usedLabels.map(l => {
              const active = filterLabelId === l.label_id;
              return (
                <button
                  key={l.label_id}
                  type="button"
                  onClick={() => setFilterLabelId(active ? 'all' : l.label_id)}
                  style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 13,
                    border: `1px solid ${l.color || '#888'}`,
                    background: active ? (l.color || '#888') : 'transparent',
                    color: active ? '#fff' : 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  {l.name} ({l.count})
                </button>
              );
            })}
          </div>
        )}

        {filteredClips.length === 0 && (
          <div className="tp-card" style={{ textAlign: 'center', color: 'var(--tp-muted)', padding: 40 }}>
            {clips.length === 0
              ? 'No clips yet. Paste a TikTok or YouTube link above to get started.'
              : 'No clips for this destination.'}
          </div>
        )}

        {/* YouTube / landscape grid */}
        {otherClips.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: tiktokClips.length > 0 ? 32 : 0 }}>
            {otherClips.map(clip => (
              <ClipCard key={clip.clip_id} clip={clip} onClick={() => setActiveClip(clip)} />
            ))}
          </div>
        )}

        {/* TikTok / portrait grid — narrower columns */}
        {tiktokClips.length > 0 && (
          <>
            {otherClips.length > 0 && (
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--tp-muted)', marginBottom: 12 }}>
                TikToks
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
              {tiktokClips.map(clip => (
                <ClipCard key={clip.clip_id} clip={clip} onClick={() => setActiveClip(clip)} />
              ))}
            </div>
          </>
        )}
      </section>

      {activeClip && (
        <ClipModal
          clip={activeClip}
          clips={filteredClips}
          destinations={destinations}
          labels={labels}
          allActivities={{ itineraryActivities, wishlistItems }}
          onClose={() => setActiveClip(null)}
          onNavigate={setActiveClip}
          onDelete={handleDelete}
          onUpdateDestinations={handleUpdateDestinations}
          onAddLabel={handleAddLabel}
          onRemoveLabel={handleRemoveLabel}
          onLinkActivity={handleLinkActivity}
          onUnlinkActivity={handleUnlinkActivity}
          onLinkWishlist={handleLinkWishlist}
          onUnlinkWishlist={handleUnlinkWishlist}
          onCreateWishlist={handleCreateAndLinkWishlist}
        />
      )}
    </>
  );
}
